import { ClipboardEvent, FC, KeyboardEvent, useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useNumericLimit } from '../useNumericLimit';
import { PunchupFieldLabel } from '../PunchupFieldLabel';
import { PunchupStepper } from '../PunchupStepper';
import styles from './PunchupNumericInput.module.css';

export type PunchupNumericUnit = 'money' | 'percent' | 'count';

export interface PunchupNumericInputProps {
	/** DOM id for the <input> + label `htmlFor`. Defaults to `name`. */
	id?: string;
	/** react-hook-form field path (supports nested paths via `get`). */
	name: string;
	label?: string;
	/** Drives the adornment ($ / % / none), the keystroke grammar, and the percent default-max rule. */
	unit: PunchupNumericUnit;
	/** Lower bound (inclusive). */
	min?: number;
	/** Upper bound (inclusive). `unit="percent"` defaults this to 100 when omitted. */
	max?: number;
	/** Stepper / clamp granularity for `unit="count"`. Default 1. */
	step?: number;
	/** For `unit="count"` only: render the field as a typable `PunchupStepper` with −/+ buttons. */
	showStepper?: boolean;
	/**
	 * `unit="count"` + `showStepper` only: allow clearing the field to empty → form value `null`
	 * (e.g. "no limit" for max-per-order / bundle size). Pair with a `placeholder` like "No limit".
	 */
	clearable?: boolean;
	disabled?: boolean;
	required?: boolean;
	placeholder?: string;
	/**
	 * Optional "explain this field" help — a help icon beside the label opening a tooltip (via
	 * `PunchupFieldLabel`). Carries the field's purpose AND any contextual limit note (e.g. the sold
	 * floor), composed by the caller — there is no separate inline caption.
	 */
	help?: string;
}

/** Whether a unit allows a decimal point (money/percent) or is integer-only (count). */
const allowsDecimal = (unit: PunchupNumericUnit): boolean => unit !== 'count';

/**
 * Sanitize free text into the field's numeric grammar. Strips currency/percent symbols, thousands
 * commas, whitespace, and letters; keeps an optional single leading `-` (so a negative min is
 * reachable) and — for money/percent — a single decimal point. Used for paste and as the canonical
 * parse path so "$1,000" and "50%" both resolve. Returns the cleaned string (not yet a number).
 */
const sanitizeNumeric = (raw: string, unit: PunchupNumericUnit): string => {
	const decimal = allowsDecimal(unit);
	// Drop everything that isn't a digit, a minus, or (when allowed) a dot.
	let cleaned = raw.replace(decimal ? /[^0-9.-]/g : /[^0-9-]/g, '');
	// Collapse to a single leading minus.
	const negative = cleaned.startsWith('-');
	cleaned = cleaned.replace(/-/g, '');
	if (negative) cleaned = `-${cleaned}`;
	// Collapse to a single decimal point (keep the first).
	if (decimal) {
		const firstDot = cleaned.indexOf('.');
		if (firstDot !== -1) {
			cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
		}
	}
	return cleaned;
};

/**
 * PunchupNumericInput
 *
 * One coherent, right-sized, limit-aware numeric field for the Punchup forms. RHF-bound via
 * `useFormContext` (watch + setValue — not register), mirroring `PunchupDateTimePicker` /
 * `PunchupColorPicker`. A `unit` prop selects the adornment ($ leading / % trailing / none) and the
 * keystroke grammar; `min`/`max` clamp the value ON BLUR (live feedback without fighting the user
 * mid-keystroke). When a blur or a stepper press caps the value, the bound's adornment / button
 * flashes once (`useNumericLimit.pulse`) and an `aria-live` line announces the adjustment.
 *
 * Width is capped to `--punchup-field-width-numeric` (content-width) so small numbers get a small box.
 */
export const PunchupNumericInput: FC<PunchupNumericInputProps> = ({
	id,
	name,
	label,
	unit,
	min,
	max,
	step = 1,
	showStepper = false,
	clearable = false,
	disabled = false,
	required,
	placeholder,
	help,
}) => {
	const {
		watch,
		setValue,
		formState: { errors, dirtyFields },
	} = useFormContext();

	// percent defaults its ceiling to 100 unless the caller gave one.
	const effectiveMax = unit === 'percent' && max === undefined ? 100 : max;
	const { clamp, pulse } = useNumericLimit({ min, max: effectiveMax });

	const fieldId = id ?? name;
	const formValue = watch(name);
	// get() resolves nested paths ("recipients[0].fee") that a flat key lookup cannot — mirrors PunchupInput.
	const error = get(errors, name);
	const hasError = Boolean(error);
	const isFieldDirty = !disabled && !!get(dirtyFields, name as string);

	// Local draft string mirrors PunchupColorPicker: while focused we don't let an external value
	// change overwrite what the user is typing; on blur we parse + clamp + re-sync.
	const [draft, setDraft] = useState<string>(() =>
		formValue === undefined || formValue === null ? '' : String(formValue)
	);
	const [focused, setFocused] = useState(false);
	// What the live region announces after a clamp; cleared when the pulse clears.
	const [announcement, setAnnouncement] = useState('');
	// One-shot animation re-trigger: bump on every clamp so the same-bound flash replays.
	const [pulseKey, setPulseKey] = useState(0);

	// Sync the draft from the form value when NOT focused (seed on load, external resets). This is the
	// seed-not-dirty mechanism: we only ever setDraft here, never setValue — so seeding the field from
	// defaultValues can't mark it dirty. setValue (with shouldDirty) happens only on user blur/step below.
	useEffect(() => {
		if (!focused) {
			setDraft(formValue === undefined || formValue === null ? '' : String(formValue));
		}
	}, [formValue, focused]);

	// Clear the announcement when the pulse one-shot clears, so it isn't re-read on a later focus.
	useEffect(() => {
		if (pulse === null) setAnnouncement('');
	}, [pulse]);

	const announce = (clamped: number, bound: 'min' | 'max') => {
		setAnnouncement(
			bound === 'max' ? `Adjusted to the maximum of ${clamped}` : `Adjusted to the minimum of ${clamped}`
		);
	};

	// Commit a numeric value to the form: clamp, mark dirty + validate, announce + pulse if capped.
	const commit = (value: number) => {
		const clamped = clamp(value);
		setValue(name, clamped, { shouldValidate: true, shouldDirty: true });
		setDraft(String(clamped));
		if (clamped > value) {
			// raised to the lower bound
			setPulseKey((k) => k + 1);
			announce(clamped, 'min');
		} else if (clamped < value) {
			// capped at the upper bound
			setPulseKey((k) => k + 1);
			announce(clamped, 'max');
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		// Always allow control / navigation keys.
		if (e.key.length > 1 || e.ctrlKey || e.metaKey || e.altKey) return;
		const decimal = allowsDecimal(unit);
		if (/[0-9]/.test(e.key)) return;
		if (e.key === '-' && (min === undefined || min < 0) && !draft.includes('-')) return;
		if (decimal && e.key === '.' && !draft.includes('.')) return;
		// Block any other printable character (letters, a second dot, $, %, comma…).
		e.preventDefault();
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text');
		setDraft(sanitizeNumeric(pasted, unit));
	};

	const handleBlur = () => {
		setFocused(false);
		const cleaned = sanitizeNumeric(draft, unit);
		// Empty / sign-only / dot-only → don't commit a NaN; revert to the form value's string.
		const parsed = parseFloat(cleaned);
		if (Number.isNaN(parsed)) {
			setDraft(formValue === undefined || formValue === null ? '' : String(formValue));
			return;
		}
		commit(parsed);
	};

	// count + showStepper delegates to the shared PunchupStepper (typable mode), so every Punchup count
	// field uses one stepper widget. The local draft / clamp / pulse machinery below drives the
	// money + percent path (and a bare count field) only.
	const isStepper = unit === 'count' && showStepper;

	const wrapperClassName = [
		styles.wrapper,
		hasError && styles.wrapperError,
		isFieldDirty && !hasError && styles.wrapperDirty,
		disabled && styles.wrapperDisabled,
	]
		.filter(Boolean)
		.join(' ');

	// The money $ / percent % adornment is a SINGLE element, so it flashes on either bound (the count
	// stepper owns its own per-button pulse).
	const adornmentPulsing = pulse !== null;
	const inputMode = unit === 'count' ? 'numeric' : 'decimal';

	return (
		<div className={styles.container}>
			{label && <PunchupFieldLabel label={label} htmlFor={fieldId} required={required} help={help} />}

			{isStepper ? (
				// Wrap in the same content-width row the $/% path uses. A bare stepper here is a cross-axis
				// child of the flex-column .container, so the default align-items: stretch would stretch its
				// bordered .root to the full form width. The row keeps it main-axis-packed at its own constant size.
				<div className={styles.controlRow}>
					<PunchupStepper
						id={fieldId}
						editable
						clearable={clearable}
						value={typeof formValue === 'number' ? formValue : null}
						onChange={(v) => setValue(name, v, { shouldValidate: true, shouldDirty: true })}
						onClear={() => setValue(name, null, { shouldValidate: true, shouldDirty: true })}
						min={min}
						max={effectiveMax}
						step={step}
						disabled={disabled}
						error={hasError}
						placeholder={placeholder}
					/>
				</div>
			) : (
				<div className={styles.controlRow}>
					<div className={wrapperClassName}>
						{unit === 'money' && (
							<span
								className={[styles.adornment, styles.adornmentLeading, adornmentPulsing ? styles.pulse : '']
									.filter(Boolean)
									.join(' ')}
								key={`money-${adornmentPulsing ? pulseKey : 'idle'}`}
								aria-hidden
							>
								$
							</span>
						)}
						<input
							id={fieldId}
							name={name}
							type="text"
							inputMode={inputMode}
							className={styles.input}
							value={draft}
							placeholder={placeholder}
							disabled={disabled}
							aria-invalid={hasError || undefined}
							onFocus={() => setFocused(true)}
							onChange={(e) => setDraft(sanitizeNumeric(e.target.value, unit))}
							onKeyDown={handleKeyDown}
							onPaste={handlePaste}
							onBlur={handleBlur}
						/>
						{unit === 'percent' && (
							<span
								className={[styles.adornment, styles.adornmentTrailing, adornmentPulsing ? styles.pulse : '']
									.filter(Boolean)
									.join(' ')}
								key={`pct-${adornmentPulsing ? pulseKey : 'idle'}`}
								aria-hidden
							>
								%
							</span>
						)}
					</div>
				</div>
			)}

			{error?.message && <span className="punchupFormError">{error.message as string}</span>}

			{!isStepper && (
				<span className={styles.srOnly} aria-live="polite" role="status">
					{announcement}
				</span>
			)}
		</div>
	);
};
